import { compile, parse } from 'path-to-regexp';
import { FetchApiError, GenericError, RecoverableApiError, } from '../errors';
import { parseApiErrorResponse } from './response';
export function assembleHeadersFromContext(context) {
    const headers = new Headers();
    if (!context)
        return headers;
    if (context.businessProcessId) {
        headers.append('x-business-process-id', context.businessProcessId);
    }
    if (context.cookies) {
        context.cookies.forEach((cookie) => {
            headers.append('cookie', cookie);
        });
    }
    if (context.headers) {
        Object.entries(context.headers).forEach(([name, value]) => {
            if (value && !Array.isArray(value))
                headers.append(name, value);
        });
    }
    return headers;
}
function stringifyValue(value) {
    switch (typeof value) {
        case 'string':
        case 'undefined': {
            return value;
        }
        case 'boolean':
            return value ? 'true' : 'false';
        case 'number':
            return String(value);
        case 'object': {
            if (value === null)
                return null;
            if (Array.isArray(value)) {
                return value
                    .map((v) => stringifyValue(v))
                    .filter((v) => typeof v === 'string');
            }
            throw new Error('Invalid api request variable type (object)');
        }
        default:
            throw new Error(`Invalid api request variable type (${typeof value})`);
    }
}
/**
 * Converts api request variables to the internally accepted format
 */
// TODO: write unit tests
function stringifyVars(vars) {
    // remove the theoretic boolean (only used as generic type) and ensure that we can work on an object (better be safe in runtime)
    if (typeof vars !== 'object' || !vars)
        return {};
    const converted = {};
    for (const key in vars) {
        converted[key] = stringifyValue(vars[key]);
    }
    return converted;
}
function getUnusedVarsForQueryString(path, vars) {
    if (!vars)
        return [];
    const queryNames = Object.keys(vars);
    if (queryNames.length === 0)
        return [];
    parse(path).forEach((token) => {
        if (typeof token === 'string')
            return;
        const name = String(token.name);
        const i = queryNames.indexOf(name);
        if (i >= 0) {
            delete queryNames[i];
        }
    });
    if (queryNames.length === 0)
        return [];
    const queryVars = [];
    queryNames.forEach((name) => {
        const value = vars[name];
        if (value === undefined || value === null)
            return;
        if (Array.isArray(value)) {
            value.forEach((v) => {
                queryVars.push([name, v]);
            });
        }
        else {
            queryVars.push([name, value]);
        }
    });
    return queryVars;
}
async function parseResponse(res, defaultContentType) {
    const rawContentType = res.headers.get('Content-Type') || defaultContentType;
    // do we have a response?
    if (res.status === 204)
        return undefined;
    const [contentType] = rawContentType.split(';');
    if (contentType === 'application/json') {
        return (await res.json());
    }
    if (contentType === 'text/plain') {
        return (await res.text());
    }
    return (await res.blob());
}
export function assembleApiUrl({ baseUrl, request, params, }) {
    // assemble url
    const toPath = compile(request.route, { encode: encodeURIComponent });
    const processedVars = request.processVars
        ? request.processVars(params)
        : params.vars;
    const vars = typeof processedVars === 'object' && processedVars
        ? processedVars
        : undefined;
    const stringifiedVars = stringifyVars(vars);
    const urlPath = toPath(stringifiedVars);
    let url = `${baseUrl}${urlPath}`;
    const queryVars = getUnusedVarsForQueryString(request.route, stringifiedVars);
    if (queryVars.length > 0) {
        const query = new URLSearchParams(queryVars).toString();
        if (query) {
            const sep = url.includes('?') ? '&' : '?';
            url += sep + query;
        }
    }
    return url;
}
/**
 * Generic fetch api implementation. Don't use this directly unless you know what you are doing!
 *
 * Only throws FetchApiError to add details of the request and response.
 */
export async function fetchGenericApi({ baseUrl, request, params, errorSource, options, }) {
    let url;
    let res;
    const method = request.method ?? 'GET';
    try {
        // assemble url to fetch
        if (!baseUrl) {
            throw new Error('Invalid base url found');
        }
        const url = assembleApiUrl({ baseUrl, request, params });
        // assemble processed body
        const body = request.processBody
            ? request.processBody(params)
            : params.body;
        // assemble headers
        const headers = assembleHeadersFromContext(options?.context);
        // only add the content type if a body is present
        const contentType = request.contentType ?? 'application/json';
        if (body &&
            typeof body === 'object' &&
            // don't interfere with multipart/form-data, so the browser will add the boundary postfix
            (typeof window !== 'object' || contentType !== 'multipart/form-data')) {
            headers.append('content-type', contentType);
        }
        headers.append('accept', request.accept ?? '*/*');
        // merge optional headers
        if (options?.headers) {
            for (const name in options.headers) {
                const value = options.headers[name];
                if (!value)
                    continue;
                if (Array.isArray(value)) {
                    value.forEach((v) => {
                        headers.append(name, v);
                    });
                }
                else {
                    headers.append(name, value);
                }
            }
        }
        // debug output
        if (process.env.NODE_ENV === 'development') {
            console.debug(`fetchApi in ${errorSource.location}: ${method} ${url}${body ? ` with body (${contentType})` : ''} | BPId: ${options?.context?.businessProcessId ?? 'n/a'}`
            /*, { body, headers: headers.entries() } */
            );
        }
        // fetch call
        res = await fetch(url, {
            method,
            body: body
                ? contentType === 'application/json'
                    ? JSON.stringify(body)
                    : body
                : null,
            headers,
            mode: 'cors',
            credentials: 'same-origin',
            referrerPolicy: 'no-referrer',
            signal: options?.signal,
            cache: 'no-cache',
        });
        // error response handling
        if (res.headers.get('x-api-error') === 'true') {
            const error = await parseApiErrorResponse(res);
            // is this error recoverable?
            if (request.recoverableErrors === true ||
                request.recoverableErrors?.includes(res.status)) {
                const recoverableError = new RecoverableApiError({
                    message: 'Recoverable api request error',
                    request: { url, method },
                    response: { status: res.status },
                    source: errorSource,
                    parent: error,
                });
                // call recover callback, if available
                if (!request.onRecoverableError) {
                    // rethrow the recoverable error so it needs to be handled by the caller
                    throw recoverableError;
                }
                const recoverResult = request.onRecoverableError?.(recoverableError, params);
                if (recoverResult === 'unrecoverable') {
                    throw new GenericError({
                        message: 'Unrecoverable api error',
                        parent: error,
                        source: errorSource,
                    });
                }
                return recoverResult;
            }
            // invoke custom error response handler, if available (we expect this to throw)
            request.processErrorResponse?.(error, {
                url,
                method,
                headers: res.headers,
                status: res.status,
            });
            // if the custom error response handler did not throw, we handle the error ourselves
            throw new GenericError({
                message: 'Api responded with an error',
                parent: error,
                source: errorSource,
            });
        }
        // parse response
        let parsedRes;
        try {
            parsedRes = await parseResponse(res, request.accept ?? 'application/json');
        }
        catch (error) {
            throw new GenericError({
                message: `Failed to parse response via content type: ${res.headers.get('Content-Type') ||
                    (request.accept ?? 'application/json')}`,
                parent: GenericError.fromUnknown(error, errorSource),
            });
        }
        // validate response, if available
        if (request.validateResponse?.(parsedRes, res) === false) {
            throw new Error('Custom response validation failed');
        }
        // process response, if available
        try {
            return request.processResponse
                ? request.processResponse(parsedRes, {
                    url,
                    method,
                    headers: res.headers,
                    status: res.status,
                })
                : parsedRes; // TResponse can be undefined for a 204 response, but that needs to be handled by the response type of the api request object
        }
        catch (error) {
            throw new GenericError({
                message: 'Custom response processing failed',
                parent: GenericError.fromUnknown(error, errorSource),
            });
        }
    }
    catch (error) {
        if (error instanceof RecoverableApiError)
            throw error;
        // TODO: need to remove all sensitive information in Stacktrace. Task 80029
        throw new FetchApiError({
            message: options?.errorMessage ?? 'Api request failed',
            request: { url: url ?? 'n/a', method },
            response: res
                ? {
                    status: res.status,
                }
                : undefined,
            source: errorSource,
            parent: GenericError.fromUnknown(error, errorSource),
        });
    }
}
